/**
 * CoreFieldLayout.tsx — silnik formatek kontrahenta (kartoteki), Commit 1.
 *
 * Neutralne primitives layoutu pól rdzeniowych (Field, Section) i typ
 * CoreFieldMeta, wcześniej zduplikowane (byte-identical) w
 * `documents/documentCoreRenderers.tsx` i `contractors/contractorCoreRenderers.tsx`.
 * Czysty move — zero zmian JSX/className/zachowania.
 */

import { type ReactNode } from 'react';

import { cn } from '@/lib/utils';

export interface CoreFieldMeta {
  slug: string;
  name: string;
  isRequired: boolean;
}

export function Section({ title, children }: { title: string; children: ReactNode }) {
  return (
    <div className="rounded-lg border border-border-secondary bg-bg-primary p-5">
      <h3 className="mb-4 text-sm font-semibold text-text-primary">{title}</h3>
      <div className="grid grid-cols-1 gap-4 md:grid-cols-2">{children}</div>
    </div>
  );
}

export function Field({
  label,
  children,
  required,
  colSpan,
  description,
  error,
}: {
  label: string;
  children: ReactNode;
  required?: boolean;
  colSpan?: 1 | 2;
  description?: string;
  /** Komunikat błędu walidacji pod polem (czerwony tekst + czerwona etykieta). `undefined` = brak błędu. */
  error?: string;
}) {
  return (
    <div className={colSpan === 2 ? 'md:col-span-2' : ''}>
      <label className={cn('mb-1 block text-xs', error ? 'text-red-600' : 'text-text-tertiary')}>
        {label}
        {required ? ' *' : ''}
      </label>
      {description ? <p className="mb-1 text-xs text-text-muted">{description}</p> : null}
      {children}
      {error ? (
        <p className="mt-1 text-xs text-red-600" role="alert">
          {error}
        </p>
      ) : null}
    </div>
  );
}
